import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { removeEntities, updateEntity, withEntities } from '@ngrx/signals/entities';
import { NotificationType } from '../enums/NotificationId';
import { TherapyJobNotification } from '../models/TherapyJobNotification';
import { FilterNotificationPipe } from '../pipes/FilterNotifications';
import { getCriticalIndex, getCriticalType } from '../utils/getCriticalNotificationType';

import { combineLatest, map, Observable, take, tap } from 'rxjs';
import { DashboardColumn } from '../constants/defaultColumns';
import { Configuration } from '../models/Configuration';
import { SystemNotification } from '../models/Notification';
import { TherapyJobV2 } from '../models/TherapyJobV2';
import { AcknowledgeItem, ActivityLogService } from '../services/activity-log.service';
import { ConfigService, ConfigurationResponse } from '../services/config.service';
import { LocaleService } from '../services/locale.service';
import { PatientService } from '../services/patient.service';
import { SystemNotificationsService } from '../services/system-notifications.service';
import { columnNameByStatus } from '../utils/getColumnByStatus';
import { buildNotification, checkAllNotificationConditionsPerJob } from '../utils/notifications/NotificationFunctions';
import { enabledBENotifications } from '../utils/notifications/enabledBENotifications';

export const THIRTY_MINUTES = 30 * 60 * 1000;

interface NotificationsState {
  loading: boolean;
  systemNotifications: Array<SystemNotification>;
  acknowledgments: { [key: string]: AcknowledgeItem };
}

const initialNotificationSate: NotificationsState = {
  loading: false,
  systemNotifications: [],
  acknowledgments: {}
};

const filterNotificationPipe = new FilterNotificationPipe();

export const NotificationsStore = signalStore(
  { providedIn: 'root' },
  withEntities<TherapyJobNotification>(),
  withDevtools('notifications'),
  withState(initialNotificationSate),
  withMethods(
    (
      store,
      config = inject(ConfigService),
      patientService = inject(PatientService),
      localeService = inject(LocaleService)
    ) => ({
      buildApplicationNotifications(therapyJobs: Array<TherapyJobV2>, configuration: Configuration) {
        if (!configuration) {
          console.warn('Configuration is undefined, cannot set notifications');
          return;
        }

        therapyJobs.forEach((therapyJob) => {
          checkAllNotificationConditionsPerJob(therapyJob, store, configuration, localeService.locale());
        });
      },
      buildSystemNotifications(therapyJobs: Array<TherapyJobV2>, configuration: Configuration) {
        if (!configuration) {
          console.warn('Configuration is undefined, cannot set notifications');
          return;
        }
        // Build system notifications, including jobs that are not in the store
        store
          .systemNotifications()
          ?.filter((n) => enabledBENotifications.includes(NotificationType?.[n?.eventName]))
          ?.forEach((notification) => {
            const jobRef: Partial<TherapyJobV2> = therapyJobs.find((tj) => tj.id === notification.therapyJobId) || {
              id: notification.therapyJobId,
              monitorDeviceId: notification.deviceId,
              patient: patientService.patientMap()[notification.patientId] || undefined
            };

            const notifyAnyways = true;
            buildNotification(
              NotificationType[notification?.eventName],
              jobRef,
              store,
              configuration,
              notifyAnyways,
              localeService.locale()
            );
          });
      },
      cleanSystemNotifications() {
        const activeNotifications = store.systemNotifications()?.map((n) => `${n?.eventName}-${n?.therapyJobId}`);

        patchState(
          store,
          removeEntities(({ id, type }) => {
            const alreadyActive = activeNotifications?.includes(id);
            return !alreadyActive && enabledBENotifications.includes(type);
          })
        );
      },
      cleanApplicationNotifications(therapyJobIds: Array<string>) {
        if (!therapyJobIds?.length) {
          return;
        }
        patchState(
          store,
          removeEntities(({ therapyJobId }) => !therapyJobIds.includes(therapyJobId))
        );
      },
      getNotificationLevelById(therapyJobId: string): number {
        // we use the index for sorting
        return getCriticalIndex(filterNotificationPipe.transform(store.entityMap(), therapyJobId));
      },
      update(id: string, partial: Partial<TherapyJobNotification>) {
        patchState(store, updateEntity({ id, changes: partial }));
      },
      notify(therapyJob: TherapyJobV2, type: NotificationType): void {
        const configuration = config.configuration()?.content;
        buildNotification(type, therapyJob, store, configuration, false, localeService.locale());
      },
      refreshJobNotifications(therapyJob: TherapyJobV2): void {
        const configuration = config.configuration()?.content;

        checkAllNotificationConditionsPerJob(therapyJob, store, configuration, localeService.locale());
      },
      deleteNotificationsByJobId(therapyJobId: string): void {
        patchState(
          store,
          removeEntities(
            store.entities().reduce((acc, n: TherapyJobNotification) => {
              if (n.id.endsWith(`-${therapyJobId}`)) {
                acc.push(n.id);
              }
              return acc;
            }, [])
          )
        );
      },
      setSystemNotifications(systemNotifications: Array<SystemNotification>) {
        patchState(store, { systemNotifications });
      },
      setAcknowledgments(acknowledgments: Array<AcknowledgeItem>) {
        patchState(store, {
          acknowledgments: acknowledgments.reduce((map, item) => {
            map[item.therapyJobId] = item;
            return map;
          }, {})
        });
      }
    })
  ),
  withMethods(
    (
      store,
      activityLogService = inject(ActivityLogService),
      systemNotificationsService = inject(SystemNotificationsService),
      config = inject(ConfigService)
    ) => ({
      // here we request all the relevant data to build the notifications
      init(therapyJobs: Array<TherapyJobV2>): Observable<void> {
        const requests = combineLatest([
          systemNotificationsService.get().pipe(take(1)),
          activityLogService
            .getPendingAcknowledgement(
              therapyJobs.filter((e) => columnNameByStatus(e?.status) !== DashboardColumn.COMPLETED).map((e) => e.id)
            )
            .pipe(take(1)),
          config.getConfig().pipe(take(1))
        ]);

        return requests.pipe(
          take(1),
          tap(
            ([systemNotifications, pendingAck, configRes]: [
              Array<SystemNotification>,
              Array<AcknowledgeItem>,
              ConfigurationResponse
            ]) => {
              store.setAcknowledgments(pendingAck);
              store.setSystemNotifications(systemNotifications);

              const configuration = configRes.content;

              store.cleanSystemNotifications();
              store.buildSystemNotifications(therapyJobs, configuration);
              store.cleanApplicationNotifications(therapyJobs.map((tj) => tj.id));
              store.buildApplicationNotifications(therapyJobs, configuration);
            }
          ),
          map(() => null)
        );
      },

      // here we re-use the stored state of the data to rebuild the notifications
      refreshAll(therapyJobs: Array<TherapyJobV2>) {
        const configuration = config.configuration()?.content;

        store.cleanSystemNotifications();
        store.buildSystemNotifications(therapyJobs, configuration);
        store.cleanApplicationNotifications(therapyJobs.map((tj) => tj.id));
        store.buildApplicationNotifications(therapyJobs, configuration);
      }
    })
  ),
  withComputed((state) => ({
    count: computed(() => {
      return state.entities()?.filter((notification) => notification.showAlarmCenter)?.length;
    }),
    criticality: computed(() => {
      const reduced = state.entities().filter((notification) => notification.showAlarmCenter);

      return getCriticalType(reduced);
    })
  }))
);

export type NotificationsStore = InstanceType<typeof NotificationsStore>;
